/* eslint-disable no-unreachable */
import { useMutation } from "@apollo/client";
import { Form, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { UPDATE_LEAD_OPPORTUNITY } from "../../graphql/mutation";
import useApollo from "../../hooks/useApollo";
import useOpportunityUserTypes from "../../hooks/useOpportunityUserTypes";
import { checkAccessPermission, closeDialog } from "../../library/helpers";
import CustomEmpty from "../commonComponents/CustomEmpty";
import CustomSkeleton from "../commonComponents/CustomSkeleton";
import DialogFooter from "../commonComponents/dialogFooter";
import UserSelector from "../commonComponents/userSelector";
import { AccessPermissionAction } from "../../library/constants";
import ReassignOwnerModal, {
  getLeadLabel,
  getVehicleLabel,
  ReassignTransferSummary,
} from "./reassignOwnerModal";
import {
  buildLeadOpportunityUsersPayload,
  getTransferAffectedQueries,
  isOwnerChanged,
} from "../../helpers/reassignOwnerHelpers";

export default function LeadOpportunityUsersForm(props) {
  const { opportunity, refetch, refetchLeads } = props;
  const [form] = Form.useForm();
  const { leadOpportunityUsers } = opportunity;
  const [userFormData, setuserFormData] = useState([]);
  const { refetchQueries } = useApollo();

  // UserSelector fetches its own user list (and appends deactivated assignees),
  // so the form no longer pulls one of its own — the old list only fed the
  // plain <Select> in the fallback branch this replaces.
  const canAddEditOpportunity = checkAccessPermission(
    AccessPermissionAction.LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT
  );
  // Only the ACTIVE assignments count as "already assigned" — same rule the
  // information form applies.
  const activeLeadUsers = useMemo(
    () => (leadOpportunityUsers || []).filter(item => item?.isActive),
    [leadOpportunityUsers]
  );

  // Resolved against the ROOT of the opportunity type's parent chain, which is
  // where user types are configured. The old query here passed no
  // `opportunityTypeId` at all, so an opportunity with no users assigned yet
  // had nothing to render and the dialog came up empty.
  const { userTypes, loading: userTypesLoader } = useOpportunityUserTypes({
    opportunityTypeId: opportunity?.opportunityTypeId || opportunity?.opportunityType?.id,
    locationId: opportunity?.location?.id,
    skip: activeLeadUsers.length > 0,
  });

  // Existing assignments win; the configured types are the blank-slate case.
  const dataSource = activeLeadUsers.length > 0 ? activeLeadUsers : userTypes;

  // --- Reassign-owner confirmation -----------------------------------------
  // Changing an owner that is ALREADY assigned goes through a confirmation
  // dialog that also collects the two "transfer to new assignee" toggles.
  // Filling an empty slot (blank-slate user type, or a row that has no user
  // yet) applies straight away — there is no outgoing owner to transfer from.
  const [userOptions, setUserOptions] = useState([]);
  const [reassign, setReassign] = useState({ open: false });

  // Writes the selection (plus the transfer flags, when it came from the
  // dialog) into the row's entry of the payload state.
  const applyUserSelection = (index, item, isUserType, userTypeName, value, transfers = null) =>
    setuserFormData(prev => {
      const temp = structuredClone(prev);
      // `userFormData` is the FULL `leadOpportunityUsers` list while the
      // rendered list is only the ACTIVE ones, so the two indexes diverge as
      // soon as the opportunity has an inactive assignment — by index we'd
      // overwrite the wrong row and save that. Match existing rows on their
      // leadOpportunityUser id; blank user-type slots have no id and keep the
      // index.
      const targetIndex = isUserType
        ? index
        : (() => {
            const found = temp.findIndex(el => el?.id && +el.id === +item.id);
            return found === -1 ? index : found;
          })();
      temp[targetIndex] = {
        ...temp[targetIndex],
        userTypeId: isUserType ? +item.id : +item.userTypeId,
        userId: value,
        userType: userTypeName,
        ...(transfers
          ? {
              isTaskAssignment: transfers.isTaskAssignment,
              isAppointmentAssignment: transfers.isAppointmentAssignment,
            }
          : {}),
      };
      return temp;
    });

  const handleUserChange = (index, item, isUserType, userTypeName, fieldName, value) => {
    // Same id-vs-index rule as applyUserSelection — read the owner currently on
    // THIS row, not whatever sits at the same position in the full list.
    const currentRow = isUserType
      ? userFormData?.[index]
      : userFormData?.find(el => el?.id && +el.id === +item.id);
    const currentUserId = currentRow?.userId ?? item?.userId;
    // A row with no `id` is a NEW assignment, not a change: no outgoing owner,
    // nothing to hand over, so no dialog.
    const isReassignment =
      !isUserType && !!item?.id && currentUserId && value && +value !== +currentUserId;
    if (!isReassignment) {
      applyUserSelection(index, item, isUserType, userTypeName, value);
      return;
    }
    setReassign({
      open: true,
      index,
      item,
      isUserType,
      userTypeName,
      fieldName,
      nextUserId: +value,
      previousUserId: +currentUserId,
    });
  };

  // Reopen the dialog for a reassignment that is already staged, so the rep can
  // amend the transfer options without having to re-pick the owner.
  const handleEditTransfers = (index, item, userTypeName, fieldName, row) =>
    setReassign({
      open: true,
      isEditing: true,
      index,
      item,
      isUserType: false,
      userTypeName,
      fieldName,
      nextUserId: +row.userId,
      isTaskAssignment: !!row.isTaskAssignment,
      isAppointmentAssignment: !!row.isAppointmentAssignment,
    });

  const handleReassignCancel = () => {
    // Amending an existing choice: the owner change stands, so just close.
    // Confirming a NEW change: put the selector back on the owner still assigned.
    if (!reassign.isEditing && reassign.fieldName) {
      form.setFieldsValue({ [reassign.fieldName]: reassign.previousUserId });
    }
    setReassign({ open: false });
  };

  const handleReassignConfirm = ({ userId, isTaskAssignment, isAppointmentAssignment }) => {
    if (reassign.fieldName) form.setFieldsValue({ [reassign.fieldName]: +userId });
    applyUserSelection(
      reassign.index,
      reassign.item,
      reassign.isUserType,
      reassign.userTypeName,
      +userId,
      { isTaskAssignment, isAppointmentAssignment }
    );
    setReassign({ open: false });
  };
  useEffect(() => {
    const ownerObj = {};
    for (let index = 0; index < opportunity?.leadOpportunityUsers?.length; index++) {
      const element = opportunity.leadOpportunityUsers[index];
      ownerObj[`leadOpportunityUsers-${element.id}`] = +element.userId;
    }
    form.setFieldsValue({ ...ownerObj });
    setuserFormData(opportunity?.leadOpportunityUsers || []);
  }, [opportunity]);
  const [updateLeadOpportunity, { loading: updateOpportunityLoader }] =
    useMutation(UPDATE_LEAD_OPPORTUNITY);
  const handleSave = async () => {
    const affectedQueries = getTransferAffectedQueries(
      userFormData,
      opportunity?.leadOpportunityUsers
    );

    const res = await updateLeadOpportunity({
      variables: {
        leadOpportunityId: opportunity.id,
        // EAI-1877: flags ride on the rows, only where the owner changed.
        leadOpportunityUsers: buildLeadOpportunityUsersPayload(
          userFormData,
          opportunity?.leadOpportunityUsers
        ),
      },
    });
    if (res.data.updateLeadOpportunity.statusCode === 200) {
      message.success(res.data.updateLeadOpportunity.message);
      refetch();
      refetchLeads && refetchLeads();
      // `refetch()` covers the opportunity only; these panels hold their own
      // results and would stay on the old assignee.
      refetchQueries(affectedQueries);
      closeDialog("editLeadOpportunityUsers");
    } else {
      message.error(res.data.updateLeadOpportunity.message);
    }
  };
  return (
    <>
      <Form onFinish={handleSave} layout="vertical" form={form}>
        {/* Mirrors the "Opportunity Users" section of
            leadOpportunityInformationForm: existing ACTIVE assignments when
            there are any, otherwise a blank slot per configured user type. Both
            render through the same UserSelector — `isUserType` is what tells
            the two shapes apart. */}
        {userTypesLoader ? (
          // Shaped like the fields it replaces — a short label bar over a
          // full-width input bar, three times — so the dialog doesn't resize
          // when the real selectors arrive.
          [0, 1, 2].map(row => (
            <div key={row} className="mb-4">
              <CustomSkeleton
                variant="button"
                block={false}
                skeletonCount={1}
                skeletonWidth={96}
                skeletonHeight={14}
                skeletonMarginBottom="8px"
              />
              <CustomSkeleton
                variant="button"
                skeletonCount={1}
                skeletonWidth="100%"
                skeletonHeight={32}
                skeletonMarginBottom="0"
              />
            </div>
          ))
        ) : isEmpty(dataSource) ? (
          <div className="my-2">
            <CustomEmpty description="No opportunity users available" />
          </div>
        ) : (
          dataSource.map((item, index) => {
            const isUserType = !item.userType;
            const userTypeName = isUserType ? item.name : item.userType.name;
            // Existing rows key their form field by the leadOpportunityUser id,
            // matching what the effect above put into form.setFieldsValue.
            const formFieldName = isUserType ? undefined : `leadOpportunityUsers-${item.id}`;
            // The staged row for this assignment, and whether its owner has
            // actually been changed — that is what the summary line reports on.
            const stagedRow = isUserType
              ? null
              : userFormData?.find(el => el?.id && +el.id === +item.id);
            const showTransferSummary =
              !!stagedRow && isOwnerChanged(stagedRow, opportunity?.leadOpportunityUsers);

            return (
              <React.Fragment key={isUserType ? index : `row-${item.id}`}>
                <UserSelector
                  key={isUserType ? index : `user-${item.id}`}
                  validation
                  name={formFieldName}
                  placeholder={`Select ${userTypeName}`}
                  handleUsersSelection={value =>
                    handleUserChange(
                      index,
                      item,
                      isUserType,
                      userTypeName,
                      formFieldName || `leadOpportunityUsers-${item.id}`,
                      value
                    )
                  }
                  // The selectors all query the same company user list; keep one
                  // copy so the reassign dialog can render the same options.
                  onUsersLoaded={users => users?.length > 0 && setUserOptions(users)}
                  allowClear
                  multiple={false}
                  showAll={false}
                  disabled={!canAddEditOpportunity}
                  item={isUserType ? item : { name: userTypeName, item }}
                  extraQueryParams={{
                    isAllLocationUsers: true,
                  }}
                />
                {showTransferSummary && (
                  <ReassignTransferSummary
                    row={stagedRow}
                    disabled={!canAddEditOpportunity}
                    onEdit={() =>
                      handleEditTransfers(
                        index,
                        item,
                        userTypeName,
                        formFieldName || `leadOpportunityUsers-${item.id}`,
                        stagedRow
                      )
                    }
                  />
                )}
              </React.Fragment>
            );
          })
        )}
      </Form>
      {/* Nothing to save while the types are still loading or when there are
          none: submitting then sends an empty `leadOpportunityUsers` and shows
          a success toast for a no-op. Also gated on the edit permission, which
          previously only disabled the selectors — not the Save button. */}
      <ReassignOwnerModal
        open={!!reassign.open}
        userTypeName={reassign.userTypeName}
        leadName={getLeadLabel(opportunity?.lead)}
        vehicleLabel={getVehicleLabel(opportunity?.vehiclesOfInterest)}
        users={userOptions}
        value={reassign.nextUserId}
        // Amending an existing choice reopens with what was chosen; a new
        // reassignment falls back to the both-ticked default.
        isEditing={!!reassign.isEditing}
        defaultTransferTasks={reassign.isEditing ? reassign.isTaskAssignment : true}
        defaultTransferAppointments={reassign.isEditing ? reassign.isAppointmentAssignment : true}
        onAssign={handleReassignConfirm}
        onCancel={handleReassignCancel}
      />
      <DialogFooter
        loading={updateOpportunityLoader}
        disabled={userTypesLoader || isEmpty(dataSource) || !canAddEditOpportunity}
        handleConfirm={() => form.submit()}
        handleCancel={() => closeDialog("editLeadOpportunityUsers")}
      />
    </>
  );
}
